// Recherche des contribuables pour l'édition des avis de passage
// Chaque changement de filtre déclenche un appel API ; les critères renseignés se cumulent
import React, { useState, useMemo, useEffect, useRef } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link } from "react-router-dom";
import Select from 'react-select';
import {
  Card,
  Table,
  Row,
  Col,
  Form,
  Button,
  Pagination,
  Badge,
  Alert,
  Spinner,
  Modal
} from "react-bootstrap";
import axios from "axios";
import {
  Search,
  Funnel,
  Person,
  Phone,
  Key,
  GeoAlt,
  X,
  ArrowRepeat,
  FilePdf,
  Building,
  Buildings,
  XCircle,
  FileEarmarkCheck,
  FileEarmarkX,
  Calendar3,
  Eye
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";
import { printAvisPassage, enregistrerImpressionsAvis, describeImpressionsResult } from "./avisPassagePrint";

const AVIS_PASSAGE_ENDPOINT = `${API_BASE_URL}/taxpayers/avis-passage`;
const GENERATION_PAGE_SIZE = 500;
const LOCALITES_ENDPOINT = `${API_BASE_URL}/localites`;

const emptyAdvancedFilters = { nom: '', telephone: '', niu: '' };

const isSet = (v) => v !== null && v !== undefined && v !== '' && v !== 'null' && v !== 'undefined';

const getHeaders = () => {
  const token = sessionStorage.getItem("authToken");
  const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

// Remonte la hiérarchie des localités jusqu'au type demandé (1000 = commune, 1002 = quartier)
const findAncestorOfType = (localitesMap, localiteId, typeCode) => {
  let current = isSet(localiteId) ? localitesMap[String(localiteId)] : null;
  while (current) {
    if (current.type_code === typeCode) return current;
    current = isSet(current.parent_id) ? localitesMap[String(current.parent_id)] : null;
  }
  return null;
};

// Message d'erreur lisible à partir de la réponse de l'API (code HTTP + message renvoyé)
const describeApiError = (err, fallback) => {
  const status = err.response?.status;
  if (status === 403) return "Vous n'avez pas les droits pour consulter les contribuables.";
  if (status === 401) return "Session expirée, veuillez vous reconnecter.";
  if (status === 404) return "Route API introuvable (taxpayers/avis-passage).";
  const apiMessage = err.response?.data?.message || err.message;
  return status ? `${fallback} (HTTP ${status}${apiMessage ? ` : ${apiMessage}` : ''})` : `${fallback} (${apiMessage})`;
};

const emptyStats = {
  total: 0,
  avec_avis: { nombre: 0, pourcentage: 0 },
  sans_avis: { nombre: 0, pourcentage: 0 }
};

// Pourcentage renvoyé par l'API (ex. 20.68) affiché avec une décimale, comme la liste des contribuables
const formatPourcentage = (value) => (Number(value) || 0).toFixed(1);

const formatDateTime = (dateString) => {
  if (!dateString) return "Non spécifié";
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatContribuable = (item) => ({
  id: item.id,
  nom: item.nom_contribuable || "",
  contribuableId: item.contribuable_id ?? null,
  created_at: item.created_at || "",
  telephone: item.telephone || "",
  localite_id: item.quartier_id || item.localite_id || null,
  hasMarket: isSet(item.id_marche),
  marcheNom: isSet(item.id_marche) ? (item.marche_nom || item.nom_marche || 'Marché') : null
});

// Ajoute le quartier et la commune à partir du référentiel des localités
const withLocalites = (localitesMap, c) => ({
  ...c,
  quartierNom: findAncestorOfType(localitesMap, c.localite_id, 1002)?.nom || "",
  communeNom: findAncestorOfType(localitesMap, c.localite_id, 1000)?.nom || ""
});

// Critères de recherche au format attendu par l'API : quartier_id, telephone, contribuable_id (NIU)
const buildSearchParams = ({ advancedFilters, selectedQuartier }, page, perPage) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('per_page', perPage);
  params.append('sort_field', 'nom_contribuable');
  params.append('sort_direction', 'asc');
  if (advancedFilters.nom) params.append('nom', advancedFilters.nom);
  if (advancedFilters.telephone) params.append('telephone', advancedFilters.telephone);
  if (advancedFilters.niu) params.append('contribuable_id', advancedFilters.niu);
  if (selectedQuartier) params.append('quartier_id', selectedQuartier.value);
  return params;
};

export default function AvisPassage() {
  const [contribuables, setContribuables] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [stats, setStats] = useState(emptyStats);
  const [localitesMap, setLocalitesMap] = useState({});
  const [quartiers, setQuartiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Filtres (cumulables), renseignés via le modal : nom / téléphone / NIU, quartier, marché
  const [selectedQuartier, setSelectedQuartier] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(emptyAdvancedFilters);

  // Brouillon du modal, appliqué au clic sur « Rechercher »
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [draftFilters, setDraftFilters] = useState({ ...emptyAdvancedFilters, quartier: null });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Sélection conservée d'une page / d'une recherche à l'autre : { [id]: contribuable }
  const [selected, setSelected] = useState({});
  const [generating, setGenerating] = useState(false);
  const abortRef = useRef(null);

  // Référentiel des localités (quartiers pour le filtre, noms pour l'affichage)
  useEffect(() => {
    axios.get(LOCALITES_ENDPOINT, { headers: getHeaders() })
      .then(response => {
        let all = [];
        if (Array.isArray(response.data)) all = response.data;
        else if (Array.isArray(response.data?.data)) all = response.data.data;

        const map = {};
        all.forEach(l => { map[String(l.localite_id)] = l; });
        setLocalitesMap(map);
        setQuartiers(all.filter(l => l.type_code === 1002));
      })
      .catch(err => console.error("❌ Erreur chargement localités:", err));
  }, []);

  // Retour à la première page quand un filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [advancedFilters, selectedQuartier, itemsPerPage]);

  // Recherche côté API
  useEffect(() => {
    const params = buildSearchParams({ advancedFilters, selectedQuartier }, currentPage, itemsPerPage);

    const runSearch = async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${AVIS_PASSAGE_ENDPOINT}?${params.toString()}`, {
          headers: getHeaders(),
          signal: controller.signal
        });
        if (response.data?.success) {
          setContribuables((response.data.data || []).map(formatContribuable));
          setPagination(response.data.pagination || { current_page: 1, last_page: 1, total: (response.data.data || []).length });
          setStats({ ...emptyStats, ...response.data.stats });
        } else {
          setContribuables([]);
          setStats(emptyStats);
          setError(response.data?.message || "Erreur lors de la recherche des contribuables.");
        }
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error("❌ Erreur recherche contribuables:", err);
        setContribuables([]);
        setStats(emptyStats);
        setError(describeApiError(err, "Erreur lors de la recherche des contribuables"));
      } finally {
        if (abortRef.current === controller) setLoading(false);
      }
    };

    runSearch();
  }, [advancedFilters, selectedQuartier, currentPage, itemsPerPage, reloadKey]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Contribuables de la page enrichis avec quartier / commune
  const rows = useMemo(() => contribuables.map(c => withLocalites(localitesMap, c)), [contribuables, localitesMap]);

  const quartierOptions = useMemo(() => quartiers
    .map(q => {
      const commune = findAncestorOfType(localitesMap, q.localite_id, 1000);
      return { value: String(q.localite_id), label: commune ? `${q.nom} (${commune.nom})` : q.nom };
    })
    .sort((a, b) => a.label.localeCompare(b.label)), [quartiers, localitesMap]);

  const totalPages = Math.max(1, pagination.last_page || 1);
  const selectedCount = Object.keys(selected).length;
  const allPageSelected = rows.length > 0 && rows.every(r => selected[r.id]);

  const toggleSelectPage = () => {
    setSelected(prev => {
      const next = { ...prev };
      rows.forEach(r => {
        if (allPageSelected) delete next[r.id];
        else next[r.id] = r;
      });
      return next;
    });
  };

  const toggleSelect = (row) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id];
      else next[row.id] = row;
      return next;
    });
  };

  const resetFilters = () => {
    setAdvancedFilters(emptyAdvancedFilters);
    setSelectedQuartier(null);
  };

  const openFiltersModal = () => {
    setDraftFilters({ ...advancedFilters, quartier: selectedQuartier });
    setShowFiltersModal(true);
  };

  const applyFiltersModal = (e) => {
    e?.preventDefault();
    setAdvancedFilters({
      nom: draftFilters.nom.trim(),
      telephone: draftFilters.telephone.replace(/\s/g, ''),
      niu: draftFilters.niu.trim()
    });
    setSelectedQuartier(draftFilters.quartier);
    setShowFiltersModal(false);
  };

  const updateDraft = (field, value) => setDraftFilters(prev => ({ ...prev, [field]: value }));

  // Filtres actifs affichés sous la barre (retirables un par un)
  const activeFilters = [
    advancedFilters.nom && { key: 'nom', label: `Raison sociale : ${advancedFilters.nom}`, clear: () => setAdvancedFilters(f => ({ ...f, nom: '' })) },
    advancedFilters.telephone && { key: 'telephone', label: `Téléphone : ${advancedFilters.telephone}`, clear: () => setAdvancedFilters(f => ({ ...f, telephone: '' })) },
    advancedFilters.niu && { key: 'niu', label: `NIU : ${advancedFilters.niu}`, clear: () => setAdvancedFilters(f => ({ ...f, niu: '' })) },
    selectedQuartier && { key: 'quartier', label: `Quartier : ${selectedQuartier.label}`, clear: () => setSelectedQuartier(null) }
  ].filter(Boolean);

  // Enregistre l'impression de chaque avis côté API, puis ouvre l'impression.
  // L'enregistrement doit être terminé avant : la boîte d'impression du navigateur bloque la page.
  const printAndRecord = async (list) => {
    const result = await enregistrerImpressionsAvis(list.map(c => c.contribuableId));
    setNotice({
      variant: result.failed.length || result.skipped ? 'warning' : 'success',
      text: describeImpressionsResult(result)
    });
    printAvisPassage(list);
  };

  const handlePrintSelection = async () => {
    const list = Object.values(selected);
    if (list.length === 0) return;
    try {
      setGenerating(true);
      setError(null);
      setNotice(null);
      await printAndRecord(list);
    } finally {
      setGenerating(false);
    }
  };

  // Génère les avis pour tous les résultats de la recherche en cours (toutes les pages)
  const handleGenerateAll = async () => {
    try {
      setGenerating(true);
      setError(null);
      setNotice(null);
      let all = [];
      let page = 1;
      let lastPage = 1;
      do {
        const params = buildSearchParams({ advancedFilters, selectedQuartier }, page, GENERATION_PAGE_SIZE);
        const response = await axios.get(`${AVIS_PASSAGE_ENDPOINT}?${params.toString()}`, { headers: getHeaders() });
        if (!response.data?.success) throw new Error(response.data?.message || "Réponse invalide");
        all = [...all, ...(response.data.data || [])];
        lastPage = response.data.pagination?.last_page || 1;
        page++;
      } while (page <= lastPage);

      if (all.length === 0) {
        setError("Aucun contribuable à générer pour ces critères.");
        return;
      }
      await printAndRecord(all.map(item => withLocalites(localitesMap, formatContribuable(item))));
    } catch (err) {
      console.error("❌ Erreur génération avis de passage:", err);
      setError(describeApiError(err, "Erreur lors de la génération des avis de passage"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      {/* Même présentation de tableau que la liste des contribuables */}
      <style>{`
        .custom-table th {
          background: #f8f9fa;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e9ecef;
        }
        .custom-table td {
          vertical-align: middle;
          padding: 0.75rem;
          font-size: 0.85rem;
        }
        .custom-table tbody tr:hover {
          background-color: rgba(13, 110, 253, 0.04);
        }
        .rounded-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 6px !important;
          cursor: pointer;
        }
        .rounded-checkbox:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        .date-cell {
          font-size: 0.75rem;
          font-weight: 600;
          color: #2c3e50;
          background: #f1f3f5;
          display: inline-block;
          padding: 4px 8px;
          border-radius: 20px;
        }
        .avatar-circle {
          width: 29px;
          height: 29px;
          background: #0d6efd;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        }
        .contribuable-name {
          font-weight: 600;
          margin-bottom: 2px;
          text-transform: uppercase;
          font-size: 0.9rem;
        }
        .contribuable-id {
          font-size: 0.7rem;
          color: #6c757d;
        }
        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .action-btn:hover {
          transform: translateY(-2px);
        }
      `}</style>
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
            <div>
              <ol className="breadcrumb mb-1 bg-transparent p-0">
                <li className="breadcrumb-item"><Link to="/agent/contribuables/liste" className="text-decoration-none">Contribuables</Link></li>
                <li className="breadcrumb-item active text-primary">Avis de passage</li>
              </ol>
              <h4 className="fw-bold mb-0">Avis de passage</h4>
            </div>
            <div className="d-flex gap-2">
              {selectedCount > 0 && (
                <Button variant="outline-secondary" className="rounded-pill" onClick={() => setSelected({})}>
                  <X className="me-1" size={14} /> Vider la sélection
                </Button>
              )}
              {selectedCount > 0 && (
                <Button variant="outline-warning" className="rounded-pill text-dark" disabled={generating} onClick={handlePrintSelection}>
                  <FilePdf className="me-2" size={14} /> Imprimer la sélection ({selectedCount})
                </Button>
              )}
              <Button
                variant="warning"
                className="rounded-pill text-dark"
                disabled={loading || generating || !pagination.total}
                onClick={handleGenerateAll}
              >
                {generating
                  ? <><Spinner animation="border" size="sm" className="me-2" /> Génération...</>
                  : <><FilePdf className="me-2" size={14} /> Générer les avis de passage ({pagination.total || 0})</>}
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" className="shadow-sm border-0">{error}</Alert>}
          {notice && (
            <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)} className="shadow-sm border-0">
              {notice.text}
            </Alert>
          )}

          {/* Cartes statistiques avis de passage (stats renvoyées par l'API pour la recherche en cours) */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3 h-100">
                <Card.Body className="d-flex align-items-center p-4">
                  <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: 60, height: 60 }}>
                    {loading ? <Spinner animation="border" size="sm" className="text-success" /> : <FileEarmarkCheck size={28} className="text-success" />}
                  </div>
                  <div>
                    <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Contribuables avec avis</h6>
                    <h3 className="mb-0 fw-bold">{loading ? '...' : stats.avec_avis.nombre}</h3>
                    <p className="text-muted small mb-0">{loading ? 0 : formatPourcentage(stats.avec_avis.pourcentage)}% du total</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3 h-100">
                <Card.Body className="d-flex align-items-center p-4">
                  <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: 60, height: 60 }}>
                    {loading ? <Spinner animation="border" size="sm" className="text-warning" /> : <FileEarmarkX size={28} className="text-warning" />}
                  </div>
                  <div>
                    <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Contribuables sans avis</h6>
                    <h3 className="mb-0 fw-bold">{loading ? '...' : stats.sans_avis.nombre}</h3>
                    <p className="text-muted small mb-0">{loading ? 0 : formatPourcentage(stats.sans_avis.pourcentage)}% du total</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3" style={{ minHeight: '115px' }}>
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small text-muted">Répartition des avis ({loading ? '...' : stats.total})</span>
                    <span className="small fw-semibold">
                      {loading ? 'Chargement...' : `${stats.avec_avis.nombre} avec avis / ${stats.sans_avis.nombre} sans avis`}
                    </span>
                  </div>
                  <div className="d-flex" style={{ height: '8px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#e9ecef' }}>
                    <div className="bg-success" style={{ width: `${loading ? 0 : stats.avec_avis.pourcentage}%`, transition: 'width 0.5s ease' }} />
                    <div className="bg-warning" style={{ width: `${loading ? 0 : stats.sans_avis.pourcentage}%`, transition: 'width 0.5s ease' }} />
                  </div>
                  <div className="d-flex justify-content-between mt-2">
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success me-2" style={{ width: 12, height: 12, padding: 0 }} />
                      <span className="small text-muted">Avec avis</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-warning me-2" style={{ width: 12, height: 12, padding: 0 }} />
                      <span className="small text-muted">Sans avis</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filtres */}
          <Card className="shadow-sm border-0 rounded-3 mb-4">
            <Card.Body>
              <div className="d-flex gap-2 justify-content-between align-items-center flex-wrap">
                <div className="d-flex gap-2 flex-wrap">
                  {activeFilters.map(f => (
                    <Badge key={f.key} bg="primary" className="rounded-pill d-flex align-items-center py-2 px-3">
                      {f.label}
                      <X size={14} className="ms-1" style={{ cursor: 'pointer' }} onClick={f.clear} />
                    </Badge>
                  ))}
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={openFiltersModal} className="rounded-3">
                    <Funnel className="me-1" size={14} /> Filtres
                    {activeFilters.length > 0 && <Badge bg="primary" className="ms-2 rounded-pill">{activeFilters.length}</Badge>}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={resetFilters} className="rounded-3">
                    <X className="me-1" size={14} /> Réinitialiser
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setReloadKey(k => k + 1)} className="rounded-3" disabled={loading}>
                    <ArrowRepeat className="me-1" size={14} /> Actualiser
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tableau */}
          <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
            <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <Building className="text-primary me-2" size={20} />
                <span className="fw-semibold">Contribuables</span>
                <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                  {pagination.total || 0} résultat(s)
                </Badge>
              </div>
              <Form.Select size="sm" style={{ width: 140 }} value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                <option value={10}>10 par page</option>
                <option value={20}>20 par page</option>
                <option value={25}>25 par page</option>
                <option value={50}>50 par page</option>
                <option value={100}>100 par page</option>
              </Form.Select>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="custom-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          className="form-check-input rounded-checkbox"
                          checked={allPageSelected}
                          onChange={toggleSelectPage}
                          title="Sélectionner la page"
                        />
                      </th>
                      <th>Contribuable</th>
                      <th>Marché</th>
                      <th>Téléphone</th>
                      <th>Quartier</th>
                      <th>Commune</th>
                      <th>Créé le</th>
                      <th style={{ width: '80px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2 text-muted small">Recherche en cours...</p>
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <Person size={48} className="text-muted mb-3" />
                          <p className="text-muted">Aucun contribuable trouvé</p>
                        </td>
                      </tr>
                    ) : rows.map(r => (
                      <tr key={r.id}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input rounded-checkbox"
                            checked={!!selected[r.id]}
                            onChange={() => toggleSelect(r)}
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-circle me-3">
                              {r.nom?.charAt(0) || 'C'}
                            </div>
                            <div>
                              <div className="contribuable-name">
                                {r.contribuableId
                                  ? <Link to={`/agent/ContribuableDetails/${r.contribuableId}`} className="text-decoration-none text-reset">{r.nom}</Link>
                                  : r.nom}
                              </div>
                              <div className="contribuable-id">ID: {r.contribuableId || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {r.hasMarket ? (
                            <Badge bg="primary" className="d-inline-flex align-items-center px-2 py-1" style={{ fontSize: '10px', borderRadius: '20px', maxWidth: '150px' }}>
                              <Buildings size={10} className="me-1" />
                              <span className="text-truncate" style={{ maxWidth: '100px' }} title={r.marcheNom}>{r.marcheNom}</span>
                            </Badge>
                          ) : (
                            <Badge bg="warning" className="d-inline-flex align-items-center px-2 py-1" style={{ fontSize: '10px', borderRadius: '20px' }}>
                              <XCircle size={10} className="me-1" />
                              Hors marché
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Phone size={12} className="text-muted me-1" />
                            <span className="small">{r.telephone || '-'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <GeoAlt size={12} className="text-muted me-1" />
                            <span className="small">{r.quartierNom || '-'}</span>
                          </div>
                        </td>
                        <td><span className="small">{r.communeNom || '-'}</span></td>
                        <td>
                          <div className="date-cell">
                            <Calendar3 size={10} className="me-1" />
                            {formatDateTime(r.created_at)}
                          </div>
                        </td>
                        <td>
                          <Button
                            as={Link}
                            to={`/agent/ContribuableDetails/${r.contribuableId}`}
                            size="sm"
                            variant="outline-primary"
                            title="Voir le détail du contribuable"
                            className="action-btn"
                            disabled={!r.contribuableId}
                          >
                            <Eye size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            {totalPages > 1 && (
              <Card.Footer className="bg-white border-0 d-flex justify-content-between align-items-center">
                <small className="text-muted">Page {currentPage} / {totalPages}</small>
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev disabled={loading || currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} />
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => Math.abs(p - currentPage) <= 2 || p === 1 || p === totalPages)
                    .map((p, i, arr) => (
                      <React.Fragment key={p}>
                        {i > 0 && p - arr[i - 1] > 1 && <Pagination.Ellipsis disabled />}
                        <Pagination.Item active={p === currentPage} disabled={loading} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                      </React.Fragment>
                    ))}
                  <Pagination.Next disabled={loading || currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} />
                </Pagination>
              </Card.Footer>
            )}
          </Card>
        </div>

        {/* Modal filtres */}
        <Modal show={showFiltersModal} onHide={() => setShowFiltersModal(false)} centered>
          <Form onSubmit={applyFiltersModal}>
            <Modal.Header closeButton>
              <Modal.Title className="fs-5"><Funnel className="me-2 text-primary" />Filtres</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label><Building className="me-1" size={14} /> Raison sociale</Form.Label>
                <Form.Control value={draftFilters.nom} onChange={(e) => updateDraft('nom', e.target.value)} placeholder="Raison sociale du contribuable" autoFocus />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label><Phone className="me-1" size={14} /> Téléphone</Form.Label>
                <Form.Control value={draftFilters.telephone} onChange={(e) => updateDraft('telephone', e.target.value)} placeholder="Numéro de téléphone" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label><Key className="me-1" size={14} /> NIU</Form.Label>
                <Form.Control value={draftFilters.niu} onChange={(e) => updateDraft('niu', e.target.value)} placeholder="Numéro d'identification unique" />
              </Form.Group>
              <Form.Group>
                <Form.Label><GeoAlt className="me-1" size={14} /> Quartier</Form.Label>
                <Select
                  options={quartierOptions}
                  value={draftFilters.quartier}
                  onChange={(option) => updateDraft('quartier', option)}
                  placeholder="Saisir pour rechercher un quartier"
                  isClearable
                  noOptionsMessage={() => "Aucun quartier"}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setDraftFilters({ ...emptyAdvancedFilters, quartier: null })}>
                Effacer
              </Button>
              <Button variant="primary" type="submit">
                <Search className="me-1" size={14} /> Rechercher
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
